import {
  getAppointmentPractitionerById,
  practitionerDisplayName,
} from "@/core/practitioners/practitioner-store";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { shouldAutoSeedDemoData } from "@/core/demo/is-demo-mode-enabled";
import { listPatients } from "@/core/patients/patient-store";
import type { Appointment, AppointmentInput, AppointmentStatus } from "./types";

export type { Appointment, AppointmentInput, AppointmentStatus } from "./types";
export {
  APPOINTMENT_DURATION_OPTIONS,
  APPOINTMENT_STATUS_LABELS,
  SERVICE_UNITS,
  appointmentEndTime,
  formatAppointmentTimeRange,
} from "./types";

const APPOINTMENTS_KEY = "hospitol.appointments";
const DEMO_SEED_KEY = "hospitol.appointments.demoSeeded";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = getStorageItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  setStorageItem(key, JSON.stringify(value));
}

function normalizeAppointment(raw: Record<string, unknown>): Appointment {
  const duration = Number(raw.durationMinutes);
  return {
    id: String(raw.id ?? ""),
    patientId: String(raw.patientId ?? ""),
    scheduledAt: String(raw.scheduledAt ?? ""),
    durationMinutes: Number.isFinite(duration) && duration > 0 ? duration : 30,
    serviceUnit: String(raw.serviceUnit ?? ""),
    motif: String(raw.motif ?? ""),
    practitionerId: String(raw.practitionerId ?? ""),
    practitioner: String(raw.practitioner ?? ""),
    status: (raw.status as AppointmentStatus) ?? "scheduled",
    notes: String(raw.notes ?? ""),
    reminderSentAt: raw.reminderSentAt ? String(raw.reminderSentAt) : null,
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
  };
}

function readAppointments(): Appointment[] {
  return readJson<Record<string, unknown>[]>(APPOINTMENTS_KEY, []).map(normalizeAppointment);
}

function sortByScheduledAsc(a: Appointment, b: Appointment): number {
  return new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime();
}

export class AppointmentValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AppointmentValidationError";
  }
}

export function ensureDemoAppointments() {
  if (!shouldAutoSeedDemoData()) return;
  if (getStorageItem(DEMO_SEED_KEY)) return;

  const patients = listPatients();
  if (patients.length < 2) return;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);

  const nextWeek = new Date();
  nextWeek.setDate(nextWeek.getDate() + 5);
  nextWeek.setHours(14, 30, 0, 0);

  createAppointment({
    patientId: patients[0]!.id,
    scheduledAt: tomorrow.toISOString(),
    durationMinutes: 30,
    serviceUnit: "Consultation générale",
    motif: "Contrôle post-traitement",
    practitionerId: "demo-prac-mukendi",
  });

  createAppointment({
    patientId: patients[1]!.id,
    scheduledAt: nextWeek.toISOString(),
    durationMinutes: 45,
    serviceUnit: "Cardiologie",
    motif: "Suivi tension artérielle",
    practitionerId: "demo-prac-kabila",
    status: "confirmed",
  });

  setStorageItem(DEMO_SEED_KEY, new Date().toISOString());
}

export function listAppointments(): Appointment[] {
  ensureDemoAppointments();
  return readAppointments().sort(sortByScheduledAsc);
}

export function listAppointmentsByPatient(patientId: string): Appointment[] {
  return listAppointments().filter((a) => a.patientId === patientId);
}

export function listUpcomingAppointments(withinDays = 14): Appointment[] {
  const now = Date.now();
  const limit = now + withinDays * 24 * 60 * 60 * 1000;
  return listAppointments().filter((a) => {
    const t = new Date(a.scheduledAt).getTime();
    return t >= now && t <= limit && a.status !== "cancelled" && a.status !== "completed";
  });
}

export function listAppointmentsForDate(date: Date): Appointment[] {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  return listAppointments().filter((a) => {
    const slot = new Date(a.scheduledAt);
    return slot.getFullYear() === y && slot.getMonth() === m && slot.getDate() === d;
  });
}

export function getAppointmentById(id: string): Appointment | null {
  return listAppointments().find((a) => a.id === id) ?? null;
}

export function createAppointment(input: AppointmentInput): Appointment {
  if (!input.patientId?.trim()) {
    throw new AppointmentValidationError("Patient requis.");
  }
  if (!input.practitionerId?.trim()) {
    throw new AppointmentValidationError("Médecin requis.");
  }
  if (!input.motif?.trim()) {
    throw new AppointmentValidationError("Motif requis.");
  }
  if (!input.serviceUnit?.trim()) {
    throw new AppointmentValidationError("Service requis.");
  }

  const durationMinutes =
    Number.isFinite(input.durationMinutes) && input.durationMinutes > 0
      ? input.durationMinutes
      : 30;

  const practitionerRecord = getAppointmentPractitionerById(input.practitionerId);
  const practitionerName =
    input.practitioner?.trim() ||
    practitionerRecord?.displayName ||
    practitionerDisplayName(input.practitionerId, "");

  const now = new Date().toISOString();
  const appointment: Appointment = {
    id: `appt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    patientId: input.patientId,
    scheduledAt: input.scheduledAt,
    durationMinutes,
    serviceUnit: input.serviceUnit.trim(),
    motif: input.motif.trim(),
    practitionerId: input.practitionerId.trim(),
    practitioner: practitionerName,
    status: input.status ?? "scheduled",
    notes: input.notes?.trim() ?? "",
    reminderSentAt: null,
    createdAt: now,
    updatedAt: now,
  };

  const list = readAppointments();
  list.push(appointment);
  writeJson(APPOINTMENTS_KEY, list);
  return appointment;
}

export function updateAppointmentStatus(
  id: string,
  status: AppointmentStatus,
): Appointment | null {
  const list = readAppointments();
  const index = list.findIndex((a) => a.id === id);
  if (index < 0) return null;
  list[index] = {
    ...list[index]!,
    status,
    updatedAt: new Date().toISOString(),
  };
  writeJson(APPOINTMENTS_KEY, list);
  return list[index]!;
}

export function cancelAppointment(id: string): Appointment | null {
  return updateAppointmentStatus(id, "cancelled");
}

export function markAppointmentReminderSent(id: string): Appointment | null {
  const list = readAppointments();
  const index = list.findIndex((a) => a.id === id);
  if (index < 0) return null;
  const now = new Date().toISOString();
  list[index] = {
    ...list[index]!,
    reminderSentAt: now,
    updatedAt: now,
  };
  writeJson(APPOINTMENTS_KEY, list);
  return list[index]!;
}
